import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DEFAULT_ORDERS_TAB_BUCKET_FILTERS, DEFAULT_QUOTES_TAB_BUCKET_FILTERS } from "@/core/constants";
import { DateFilterId } from "@/core/enums";
import { toDateISOString } from "@/core/utilities";
import type { DateFilterType } from "@/core/types";
import type { OrdersFilterData } from "@/shared/account";
import type { Ref } from "vue";

export function useUserOrdersFilter(isQuotesPage?: boolean) {
  const { t } = useI18n();

  function getDateFilterRanges(): DateFilterType[] {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const lastMonthStartDate = new Date(currentDate);
    lastMonthStartDate.setMonth(currentDate.getMonth() - 1);
    lastMonthStartDate.setDate(1);

    const last3MonthsStartDate = new Date(currentDate);
    last3MonthsStartDate.setMonth(currentDate.getMonth() - 3);
    last3MonthsStartDate.setDate(3);

    const last6MonthsStartDate = new Date(currentDate);
    last6MonthsStartDate.setMonth(currentDate.getMonth() - 6);
    last6MonthsStartDate.setDate(6);

    const lastYearStartDate = new Date(currentDate);
    lastYearStartDate.setFullYear(currentDate.getFullYear() - 1);
    lastYearStartDate.setMonth(1);
    lastYearStartDate.setDate(1);

    const endDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    return [
      {
        id: DateFilterId.LAST_3_MONTHS,
        label: t("common.labels.past_three_months"),
        startDate: last3MonthsStartDate.toISOString(),
        endDate: endDate.toISOString(),
      },
      {
        id: DateFilterId.LAST_6_MONTHS,
        label: t("common.labels.past_six_months"),
        startDate: last6MonthsStartDate.toISOString(),
        endDate: endDate.toISOString(),
      },
      {
        id: DateFilterId.LAST_YEAR,
        label: t("common.labels.last_year"),
        startDate: lastYearStartDate.toISOString(),
        endDate: endDate.toISOString(),
      },
      {
        id: DateFilterId.ALL,
        label: t("common.labels.all_available"),
      },
    ];
  }

  const dateFilters = getDateFilterRanges();
  const defaultDateFilter = dateFilters[1];

  const filterData: Ref<OrdersFilterData> = ref({
    tabBucketTL: [],
    startDate: toDateISOString(defaultDateFilter.startDate!),
    endDate: toDateISOString(defaultDateFilter.endDate!),
  });

  function defaultTabBucketTLFilter() {
    return isQuotesPage ? DEFAULT_QUOTES_TAB_BUCKET_FILTERS : DEFAULT_ORDERS_TAB_BUCKET_FILTERS;
  }
  const appliedFilterData: Ref<OrdersFilterData> = ref({ ...filterData.value });

  function applyFilters() {
    if (JSON.stringify(appliedFilterData.value) === JSON.stringify(filterData.value)) {
      return;
    }
    appliedFilterData.value = { ...filterData.value };
  }

  function resetDataToApplied() {
    filterData.value = { ...appliedFilterData.value };
  }

  function resetFilters() {
    filterData.value = {
      tabBucketTL: [],
      startDate: toDateISOString(defaultDateFilter.startDate!),
      endDate: toDateISOString(defaultDateFilter.endDate!),
    };
    appliedFilterData.value = { ...filterData.value };
  }

  return {
    filterData,
    appliedFilterData: computed(() => appliedFilterData.value),
    dateFilterTypes: computed(() => dateFilters),
    defaultDateFilter: computed(() => defaultDateFilter),
    defaultTabBucketTLFilter,
    applyFilters,
    resetFilters,
    resetDataToApplied,
  };
}
